import { useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { listQueries, listMutations } from './lists.queries';

export type ChecklistFilter = Record<string, string | number>;

export function useChecklists(
  filters?: ChecklistFilter,
  options?: { suspense?: boolean },
) {
  return useQuery({ ...listQueries.list(filters), ...options });
}

/**
 * Fetch a checklist and its dependencies (templates).
 * Returns `data` (primary list) and `allLists` (primary + deps) for building.
 */
export function useChecklist(
  listId?: string,
  options?: { suspense?: boolean },
) {
  const query = useQuery({
    ...listQueries.detail(listId || ''),
    enabled: !!listId,
    ...options,
  });

  // query.data is List[] (primary first, then deps).
  // Expose `data` as the primary list for backwards compat,
  // and `allLists` for buildList dependency resolution.
  const data = query.data?.[0] ?? undefined;
  const allLists = query.data ?? [];

  return useMemo(() => ({ ...query, data, allLists }), [query, data, allLists]);
}

export function useWorkChecklists(workId: number) {
  return useQuery(listQueries.workChecklists(workId));
}

export function usePostListEventsMutation() {
  const mut = listMutations.postEvents();
  return useMutation({
    ...mut,
  });
}

/**
 * Fetch all templates (lists where werk_id IS NULL).
 * Reuses the checklist query infrastructure with a PostgREST filter.
 */
export function useTemplates(options?: { suspense?: boolean }) {
  return useQuery({
    ...listQueries.list({ werk_id: 'is.null' }),
    ...options,
  });
}
